import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { track } from '@amplitude/analytics-browser'
import { Panel, colors } from 'src/components/app-ui'
import useWorkspaces from 'src/hooks/useWorkspaces'
import { shopPatchAction } from 'src/reducers/shop/action'
import useFormStyles from './form-styles'

const NAME_MAX_LENGTH = 36

const useStyles = makeStyles({
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
  },
  idRow: {
    display: 'flex',
    gap: '8px',
  },
  idInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: '13px',
  },
  copy: {
    flex: 'none',
    minWidth: 84,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '0 14px',
    border: '1px solid rgba(1,95,251,.5)',
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.primary,
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    padding: '16px 20px',
    borderRadius: '12px',
    background: colors.hover,
  },
  accountText: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '13px',
    lineHeight: 1.5,
    color: colors.muted,
    '& strong': {
      fontWeight: 600,
      color: colors.navy,
    },
  },
  accountLink: {
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.primary,
    whiteSpace: 'nowrap',
  },
})

/** Workspace > Settings: name and ID. */
const SettingsTab = () => {
  const classes = useStyles()
  const form = useFormStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { currentWorkspace } = useWorkspaces()

  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef()

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  const savedName = currentWorkspace?.shopName || ''
  const workspaceId = currentWorkspace?.shopId

  useEffect(() => {
    setName(savedName)
  }, [savedName])

  const trimmed = name.trim()
  const canSave = trimmed.length >= 2 && trimmed !== savedName && !saving

  const copyId = () => {
    navigator.clipboard?.writeText(String(workspaceId ?? '')).catch(() => {})
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), 1500)
  }

  // Saved when the field loses focus or on Enter.
  const handleSave = () => {
    if (!canSave) return
    setSaving(true)
    dispatch(
      shopPatchAction({ id: workspaceId, name: trimmed }, () => {
        track('workspace_name_edit', {
          shop_id: workspaceId || null,
          old_workspace_name: savedName || null,
          new_workspace_name: trimmed,
        })
        // The workspace name is shown in the header from the shops list.
        window.location.reload()
      }),
    )
  }

  return (
    <div className={form.stack}>
      <Panel className={form.card}>
        <div className={form.cardTitle}>General</div>
        <div className={classes.fields}>
          <label className={form.field}>
            Workspace name
            <input
              className={form.input}
              value={name}
              maxLength={NAME_MAX_LENGTH}
              disabled={saving}
              onChange={event => setName(event.target.value)}
              onBlur={handleSave}
              onKeyDown={event => event.key === 'Enter' && event.target.blur()}
            />
          </label>
          <label className={form.field}>
            Workspace ID
            <div className={classes.idRow}>
              <input
                className={`${form.readOnly} ${classes.idInput}`}
                value={workspaceId ?? ''}
                readOnly
              />
              <button type="button" className={classes.copy} onClick={copyId}>
                <Icon
                  icon={copied ? 'tabler:check' : 'tabler:copy'}
                  width={13}
                  height={13}
                />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </label>
        </div>
      </Panel>

      <div className={classes.account}>
        <div className={classes.accountText}>
          <Icon icon="tabler:info-circle" width={18} height={18} />
          <span>
            Plan, AI credits and billing belong to your <strong>account</strong>
            , not this workspace.
          </span>
        </div>
        <button
          type="button"
          className={classes.accountLink}
          onClick={() => history.push('/profile')}
        >
          Open account →
        </button>
      </div>
    </div>
  )
}

export default SettingsTab
